import { useEffect } from 'react';
import type { RefObject } from 'react';

const SCROLLABLE_OVERFLOW = /(auto|scroll|overlay)/;

/** Can `el` still move in the wheel's direction on this axis? */
function canConsume(el: HTMLElement, deltaX: number, deltaY: number): boolean {
  const style = getComputedStyle(el);
  if (deltaY !== 0 && SCROLLABLE_OVERFLOW.test(style.overflowY)) {
    const room = el.scrollHeight - el.clientHeight;
    if (room > 1) {
      if (deltaY < 0 && el.scrollTop > 0) return true;
      if (deltaY > 0 && el.scrollTop < room - 1) return true;
    }
  }
  if (deltaX !== 0 && SCROLLABLE_OVERFLOW.test(style.overflowX)) {
    const room = el.scrollWidth - el.clientWidth;
    if (room > 1) {
      if (deltaX < 0 && el.scrollLeft > 0) return true;
      if (deltaX > 0 && el.scrollLeft < room - 1) return true;
    }
  }
  return false;
}

/**
 * Would this wheel escape the Concierge and scroll the host page?
 *
 * Walks from `target` up to `root`; the answer is "no" as soon as something on
 * the way can still absorb the delta on the axis being scrolled.
 */
export function shouldBlockWheelScroll(
  root: HTMLElement,
  target: Node | null,
  deltaX: number,
  deltaY: number,
): boolean {
  if (deltaX === 0 && deltaY === 0) return false;
  let node: Node | null = target;
  while (node) {
    if (node === root) break;
    if (isHtmlElement(node) && canConsume(node, deltaX, deltaY)) return false;
    // `host` keeps the walk going if the target sits in a nested shadow tree.
    node = node.parentNode ?? (node as unknown as ShadowRoot).host ?? null;
  }
  return !canConsume(root, deltaX, deltaY);
}

/** `instanceof HTMLElement` fails across realms (iframes, jsdom windows). */
function isHtmlElement(node: Node): node is HTMLElement {
  return node.nodeType === 1;
}

/**
 * Stop wheel scrolling inside the Concierge from reaching the host page.
 *
 * `overscroll-behavior: contain` is not enough on its own: it only limits
 * chaining OUT OF a container that actually consumed part of the scroll. When
 * the pointer is over content that cannot scroll at all — a short transcript,
 * the welcome screen, the composer, a transcript already at its end — the
 * browser hands the wheel straight to the nearest scrollable ancestor, which is
 * the host document. That is the reported bug: "when you scroll the panel and it
 * reaches its end, the page below starts scrolling."
 *
 * So walk from the wheel target up to `rootRef`; if nothing on the way can
 * absorb the delta, cancel the event. Anything that can scroll is left alone, so
 * the transcript, the product panel and the horizontal variant rows all keep
 * their normal behavior.
 */
export function useScrollChainGuard(rootRef: RefObject<HTMLElement | null>, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const root = rootRef.current;
    if (!root) return;

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return; // pinch-zoom gesture, not a scroll
      if (shouldBlockWheelScroll(root, event.target as Node | null, event.deltaX, event.deltaY)) {
        event.preventDefault();
      }
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    return () => root.removeEventListener('wheel', onWheel);
  }, [rootRef, enabled]);
}
